import React from "react";
import { View, StyleSheet, Animated, Easing, Platform, useWindowDimensions } from "react-native";
import { spacing } from "../../theme";
import { useColors, ThemeColors } from '../../hooks/useColors';

// Max width for phone container on web
const PHONE_CONTAINER_MAX_WIDTH = 430;

interface ProductCardSkeletonProps {
  cardWidth?: number;
}

export function ProductCardSkeleton({ cardWidth }: ProductCardSkeletonProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const windowDimensions = useWindowDimensions();
  
  // On web, constrain width to phone container max width
  const effectiveWidth = Platform.OS === 'web' 
    ? Math.min(windowDimensions.width, PHONE_CONTAINER_MAX_WIDTH)
    : windowDimensions.width;
    
  const calculatedCardWidth = cardWidth || (effectiveWidth - spacing.lg * 3) / 1.8;

  // Create animated value for shimmer effect
  const shimmerAnimation = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    const animate = () => {
      Animated.sequence([
        Animated.timing(shimmerAnimation, {
          toValue: 1,
          duration: 1000,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnimation, {
          toValue: 0,
          duration: 1000,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
      ]).start(() => animate());
    };
    animate();
  }, [shimmerAnimation]);

  const shimmerOpacity = shimmerAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.7],
  });

  const dynamicStyles = StyleSheet.create({
    card: {
      ...styles.card,
      width: calculatedCardWidth,
    },
    imageWrap: {
      ...styles.imageWrap,
      width: calculatedCardWidth,
      height: calculatedCardWidth,
    },
  });

  return (
    <View style={dynamicStyles.card}>
      {/* Image skeleton */}
      <View style={dynamicStyles.imageWrap}>
        <Animated.View
          style={[
            styles.skeletonImage,
            {
              opacity: shimmerOpacity,
            },
          ]}
        />

        {/* Price tag skeleton */}
        <Animated.View
          style={[
            styles.skeletonPriceTag,
            {
              opacity: shimmerOpacity,
            },
          ]}
        />
      </View>

      {/* Content skeleton */}
      <View style={styles.content}>
        <Animated.View
          style={[
            styles.skeletonTitle,
            {
              opacity: shimmerOpacity,
            },
          ]}
        />
        <Animated.View
          style={[
            styles.skeletonTitleSecond,
            {
              opacity: shimmerOpacity,
            },
          ]}
        />
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  card: {
    backgroundColor: colors.background,
    borderRadius: 20,
    overflow: "hidden",
    marginHorizontal: spacing.sm,
    // iOS shadow
    shadowColor: colors.shadow,
    shadowOpacity: 0.12,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 8,
    // Android shadow
    elevation: 4,
  },
  imageWrap: {
    position: "relative",
  },
  skeletonImage: {
    width: "100%",
    height: "100%",
    backgroundColor: colors.border,
  },
  skeletonPriceTag: {
    position: "absolute",
    bottom: spacing.sm,
    left: spacing.sm,
    backgroundColor: colors.border,
    borderRadius: 12,
    width: 60,
    height: 28,
  },
  content: {
    padding: spacing.md,
    height: 60,
    justifyContent: "center",
  },
  skeletonTitle: {
    height: 16,
    backgroundColor: colors.border,
    borderRadius: 8,
    marginBottom: 8,
    width: "80%",
  },
  skeletonTitleSecond: {
    height: 16,
    backgroundColor: colors.border,
    borderRadius: 8,
    width: "60%",
  },
});
